// lib/audio.js Audio capture utilities - separate from camera to avoid Android audio focus issues
import store from '~/store'

const logger = console.scope('audio')

// The mic this module attached to the sender. It outlives any stage wrapper (`window.camera_stream`), which only carries it.
let micTrack = null

/** The live mic track, for a new stage wrapper to carry. */
export const currentMicTrack = () => (micTrack?.readyState === 'live' ? micTrack : null)

/**
 * Get audio-only stream from microphone Separated from camera capture to avoid triggering Android audio focus when microphone isn't needed (which mutes other apps like Spotify)
 *
 * @param {Object} constraints - Audio constraints
 * @returns {Promise<MediaStream>} Audio-only stream
 */
export async function getAudioStream(constraints = {}) {
	const storeSettings = store.getState().settings || {}
	const audioConstraints = {
		echoCancellation: false,
		noiseSuppression: false,
		autoGainControl: false,
	}
	const deviceId = constraints.deviceId ?? storeSettings.audio_input_device
	if (deviceId) {
		audioConstraints.deviceId = { exact: deviceId }
	}
	// No explicit device set — leave deviceId unconstrained. Chrome then routes to its synthetic "default" device (which tracks the OS system default mic); a soft { ideal: 'default' } hint is spec-equivalent to no constraint and does NOT prevent the browser from picking a virtual (e.g. NDI) input, so we omit it entirely.

	logger.info('Requesting audio-only stream', audioConstraints)

	const stream = await navigator.mediaDevices.getUserMedia({
		video: false,
		audio: audioConstraints,
	})

	const audioTrack = stream.getAudioTracks()[0]
	logger.info('Audio stream acquired:', {
		trackId: audioTrack?.id,
		trackLabel: audioTrack?.label,
		trackReadyState: audioTrack?.readyState,
	})

	return stream
}

/**
 * Add audio track to camera stream and WebRTC connection
 * @param {Object} socket - Socket instance with setAudioEnabled method
 * @returns {Promise<MediaStreamTrack|null>} The audio track, or null if failed
 */
export async function enableAudio(socket) {
	try {
		const audioStream = await getAudioStream()
		const audioTrack = audioStream.getAudioTracks()[0]
		if (micTrack !== audioTrack) micTrack?.stop()
		micTrack = audioTrack

		// Add to camera stream if it exists
		if (window.camera_stream) {
			// Remove any existing audio track first
			const existingAudioTracks = window.camera_stream.getAudioTracks()
			existingAudioTracks.forEach(track => {
				window.camera_stream.removeTrack(track)
				track.stop()
			})
			window.camera_stream.addTrack(audioTrack)
		}

		// Add to WebRTC connection
		if (socket) {
			await socket.setAudioEnabled(true, audioTrack)
		}

		logger.info('Audio enabled')
		return audioTrack
	} catch (error) {
		logger.error('Failed to enable audio:', error)
		return null
	}
}

/**
 * Ensure a live audio track is attached to the peer connection's audio sender.
 *
 * The mic is normally attached by an effect in useBackgroundStream, but that can miss (permission denied, device busy, a reconnect racing the effect). Recording with an empty sender produces a silent file with no client-side error, so callers about to record should verify first.
 *
 * @param {Object} socket - Socket instance with an audioTransceiver
 * @returns {Promise<MediaStreamTrack|null>} The live audio track, or null if it couldn't be attached
 */
export async function ensureAudioAttached(socket) {
	const existing = socket?.audioTransceiver?.sender?.track
	if (existing && existing.readyState === 'live') return existing

	logger.warn('No live audio track on sender, attempting to attach', {
		hasTrack: !!existing,
		readyState: existing?.readyState ?? null,
	})
	return await enableAudio(socket)
}

/**
 * Remove audio track from camera stream and WebRTC connection
 * @param {Object} socket - Socket instance with setAudioEnabled method
 */
export async function disableAudio(socket) {
	// Remove from camera stream
	if (window.camera_stream) {
		const audioTracks = window.camera_stream.getAudioTracks()
		audioTracks.forEach(track => {
			window.camera_stream.removeTrack(track)
			track.stop()
			logger.info('Audio track removed and stopped:', track.id)
		})
	}
	micTrack?.stop()
	micTrack = null

	// Remove from WebRTC connection
	if (socket?.pc) {
		await socket.setAudioEnabled(false)
	}

	logger.info('Audio disabled')
}
